'use client';
import { useEffect } from 'react';
import { Phone, PhoneOff, Video } from 'lucide-react';

interface IncomingCallModalProps {
  isOpen: boolean;
  callerName: string;
  callType: 'audio' | 'video';
  onAccept: () => void;
  onReject: () => void;
}

export default function IncomingCallModal({ isOpen, callerName, callType, onAccept, onReject }: IncomingCallModalProps) {
  // Bring window to focus when incoming call arrives
  useEffect(() => {
    if (isOpen) {
      // Flash the document title to attract attention
      const originalTitle = document.title;
      let flashInterval: ReturnType<typeof setInterval>;
      
      if (!document.hasFocus()) {
        let showAlert = true;
        flashInterval = setInterval(() => {
          document.title = showAlert ? `📞 ${callerName} is calling...` : originalTitle;
          showAlert = !showAlert;
        }, 1000);
      }

      return () => {
        if (flashInterval) clearInterval(flashInterval);
        document.title = originalTitle;
      };
    }
  }, [isOpen, callerName]);

  if (!isOpen) return null;

  const initials = callerName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-2xl p-8 w-full max-w-sm mx-4 shadow-2xl text-center relative overflow-hidden">
        {/* Animated ring pulse background */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-40 h-40 rounded-full bg-green-100 animate-ping opacity-20" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-32 h-32 rounded-full bg-green-200 animate-ping opacity-30" style={{ animationDelay: '0.5s' }} />
        </div>

        {/* Content */}
        <div className="relative z-10">
          {/* Call type indicator */}
          <div className="flex items-center justify-center gap-1.5 mb-4">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-xs font-medium text-green-600 uppercase tracking-wider">
              Incoming {callType} call
            </span>
          </div>

          {/* Caller avatar with ring animation */}
          <div className="relative w-24 h-24 mx-auto mb-4">
            {/* Outer ring animation */}
            <div className="absolute inset-0 rounded-full border-4 border-green-400 animate-ping opacity-50" />
            <div className="absolute inset-1 rounded-full border-2 border-green-300 animate-pulse" />
            {/* Avatar */}
            <div className="absolute inset-2 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
              <span className="text-2xl font-bold text-white">{initials}</span>
            </div>
          </div>

          {/* Caller info */}
          <h3 className="text-xl font-semibold text-gray-900">{callerName}</h3>
          <p className="text-sm text-gray-500 mt-1 flex items-center justify-center gap-1.5">
            {callType === 'video' ? <Video size={14} /> : <Phone size={14} />}
            {callType === 'video' ? 'Video Call' : 'Voice Call'}
          </p>

          {/* Ringing dots animation */}
          <div className="flex items-center justify-center gap-1 mt-3">
            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-center gap-8 mt-8">
            {/* Decline */}
            <div className="flex flex-col items-center gap-1.5">
              <button
                onClick={onReject}
                className="w-16 h-16 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-all shadow-lg hover:shadow-red-200 hover:scale-105 active:scale-95"
                title="Decline"
              >
                <PhoneOff size={24} />
              </button>
              <span className="text-xs text-gray-500">Decline</span>
            </div>

            {/* Accept */}
            <div className="flex flex-col items-center gap-1.5">
              <button
                onClick={onAccept}
                className="w-16 h-16 rounded-full bg-green-500 text-white flex items-center justify-center hover:bg-green-600 transition-all shadow-lg hover:shadow-green-200 hover:scale-105 active:scale-95 animate-bounce"
                title="Accept"
              >
                {callType === 'video' ? <Video size={24} /> : <Phone size={24} />}
              </button>
              <span className="text-xs text-gray-500">Accept</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
